import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, connectWebSocket, disconnectWebSocket, requestNotificationPermission, showNotification } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const logout = useCallback(() => {
    localStorage.removeItem('mmarketing_token');
    localStorage.removeItem('mmarketing_user');
    setUser(null);
    navigate('/login');
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('mmarketing_token');
    const storedUser = localStorage.getItem('mmarketing_user');
    
    if (token && storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        localStorage.removeItem('mmarketing_user');
      }
      authAPI.getMe()
        .then(res => {
          setUser(res.data);
          localStorage.setItem('mmarketing_user', JSON.stringify(res.data));
        })
        .catch((err) => {
          // Only logout on 401 (expired/invalid token). Keep session for network errors.
          if (err.response?.status === 401) {
            logout();
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleWebSocketMessage = (data) => {
      if (data.type === 'new_flash_deal') {
        showNotification('New Flash Deal!', {
          body: `${data.deal.product_name} - ${data.deal.discount_percent}% OFF!`,
          tag: 'flash-deal'
        });
      }
    };

    connectWebSocket(handleWebSocketMessage);
    return () => disconnectWebSocket(handleWebSocketMessage);
  }, []);

  const login = async (email, password) => {
    const res = await authAPI.login({ email, password });
    const { token, user: userData } = res.data;
    localStorage.setItem('mmarketing_token', token);
    localStorage.setItem('mmarketing_user', JSON.stringify(userData));
    setUser(userData);
    requestNotificationPermission();
    return userData;
  };

  const register = async (name, email, password, role = 'member', referralCode = null, creatorRef = null) => {
    const payload = { name, email, password, role: 'member', referral_code: referralCode };
    if (creatorRef) payload.creator_ref = creatorRef;
    const res = await authAPI.register(payload);
    const { token, user: userData } = res.data;
    localStorage.setItem('mmarketing_token', token);
    localStorage.setItem('mmarketing_user', JSON.stringify(userData));
    setUser(userData);
    requestNotificationPermission();
    return userData;
  };

  const updateUser = (updates) => {
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    localStorage.setItem('mmarketing_user', JSON.stringify(updatedUser));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
